import styles from './ResourcesTimeline.module.scss';

import {
  MbscCalendarColor,
  MbscCalendarEvent,
  MbscEventClickEvent,
  MbscEventCreatedEvent,
  MbscEventDeleteEvent,
  MbscResource,
  MbscSelectedEventsChangeEvent,
} from '@mobiscroll/react';
import { Close, Groups, Save, Search, Settings } from '@mui/icons-material';
import {
  Box,
  IconButton,
  Popover,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { popoverClasses } from '@mui/material/Popover';
import clsx from 'clsx';
import { cloneDeep } from 'lodash';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Tooltip } from '@work4all/components/lib/components/tooltip/Tooltip';
import { HookedUserIcon } from '@work4all/components/lib/components/user-icon/useUserIconRegister';
import { Divider } from '@work4all/components/lib/dataDisplay/divider/Divider';
import { BaseActionButton } from '@work4all/components/lib/input/base-action-button/BaseActionButton';
import { CheckboxRadioItem } from '@work4all/components/lib/input/checkbox-radio-item';

import { usePopoverState } from '@work4all/data/lib/hooks/usePopoverState';

import { Appointment } from '@work4all/models/lib/Classes/Appointment.entity';
import { AppointmentAttendee } from '@work4all/models/lib/Classes/AppointmentAttendee.entity';
import { Project } from '@work4all/models/lib/Classes/Project.entity';
import { ProjectProcess } from '@work4all/models/lib/Classes/ProjectProcess.entity';
import { ResourceUtilizationInfo } from '@work4all/models/lib/Classes/ResourceUtilizationInfo.entity';
import { Sickness } from '@work4all/models/lib/Classes/Sickness.entity';
import { User } from '@work4all/models/lib/Classes/User.entity';
import { Vacation } from '@work4all/models/lib/Classes/Vacation.entity';
import { SdObjType } from '@work4all/models/lib/Enums/SdObjType.enum';

import {
  AppointmentQuickCreateDialog,
  AppointmentTemplate,
} from '../../../../../../../containers/calendar/components/AppointmentQuickCreateDialog';
import { CreateViewDialog } from '../../../../../../../containers/calendar/components/CreateViewDialog';
import { ViewsList } from '../../../../../../../containers/calendar/components/ViewsList';
import { useCalendarViews } from '../../../../../../../containers/calendar/hooks/use-calendar-views';
import { mapUsersToAppointmentAttendees } from '../../../../../../../containers/calendar/utils/map-users-to-appointment-attendees';
import { settings, useSetting } from '../../../../../../../settings';
import { UserCompilationPicker } from '../../../../../../entity-picker/UsersCompilationPicker';
import { TimeUnit } from '../project-planning-view/components/time-unit-switch/TimeUnitSwitch';
import { ZoomValue } from '../project-planning-view/components/timeline-zoom/TimelineZoom';
import { useColors } from '../project-planning-view/hooks/use-colors';
import { useProjectProcessEvents } from '../project-planning-view/hooks/use-project-process-events';
import { useProjectProcessResources } from '../project-planning-view/hooks/use-project-process-resources';
import { useProjectWithProcesses } from '../project-planning-view/hooks/use-project-with-processes';
import {
  ZoomableTimeline,
  ZoomableTimelineProps,
} from '../zoomable-timeline/ZoomableTimeline';

import { ResourceUtilizationInfoDialog } from './components/resource-utilization-info-dialog/ResourceUtilizationInfoDialog';
import { ResourcesTimelineToolbar } from './components/resources-timeline-toolbar/ResourcesTimelineToolbar';
import { useUsersById } from './hooks/get-users-by-id';
import { useDeleteAppointments } from './hooks/use-delete-appointments';
import { useResourceInfo } from './hooks/use-resource-info';
import { useUserAppointments } from './hooks/use-user-appointments';
import { useUtilizationEvents } from './hooks/use-utilization-events';

export interface DateRangeConf {
  start: Date;
  end: Date;
}

type ResourcesTimelineProps = Pick<
  ZoomableTimelineProps,
  | 'compactView'
  | 'date'
  | 'leftAreaCollapsed'
  | 'leftAreaWidth'
  | 'onSelectedDateChange'
  | 'componentRef'
  | 'zoom'
  | 'view'
  | 'onDestroy'
  | 'onPageLoaded'
> & {
  className?: string;
  highlightedDateRanges: DateRangeConf[];
  focusedProject?: Project;
  focusedProjectProcess?: ProjectProcess;
  userIds?: number[];
  onUsersChange?: (users: User[]) => void;
  showToolbar?: boolean;
  zoom?: ZoomValue;
  unit?: 'year' | 'day' | 'week' | 'month';
  onZoomChange?: (zoom: ZoomValue) => void;
  onTimeUnitChange?: (unit: TimeUnit) => void;
  projectIdList?: number[];
};

const resourceOptions: Partial<MbscResource> = {
  eventCreation: false,
  fixed: true,
};

export const ResourcesTimeline = (props: ResourcesTimelineProps) => {
  const {
    date: initialDate = new Date(),
    className,
    highlightedDateRanges,
    focusedProjectProcess,
    focusedProject,
    userIds: userIdsInitial,
    onUsersChange,
    showToolbar = false,
    zoom = 3,
    onZoomChange,
    unit = 'year',
    onTimeUnitChange,
    projectIdList,
    ...rest
  } = props;

  const resourcePlanningIncludeSubProjects = useSetting(
    settings.resourcePlanningIncludeSubProjects()
  );

  const resourcePlanningIncludeUserAppointments = useSetting(
    settings.resourcePlanningIncludeUserAppointments()
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [events, setEvents] = useState<MbscCalendarEvent[]>([]);

  const [selectedProcess, setSelectedProcess] = useState<ProjectProcess>(null);
  const [selectedAppointments, setSelectedAppointments] = useState<
    Appointment[]
  >([]);
  const [appointmentTemplate, setAppointmentTemplate] =
    useState<AppointmentTemplate>({});
  const [attendeeSuggestions, setAttendeeSuggestions] = useState<
    AppointmentAttendee[]
  >([]);

  const highlightedProcessIds = useMemo(() => {
    return [selectedProcess?.id];
  }, [selectedProcess?.id]);

  const { data: projectList } = useProjectWithProcesses(projectIdList);
  const projectProcessEvents = useProjectProcessEvents({
    projectList,
    compact: true,
    highlightedProcessIds,
  });

  const projectProcessResources = useProjectProcessResources({
    eventList: projectProcessEvents,
    projectList,
    compact: true,
    resourceOptions,
  });

  const [usersDialogOpen, setUsersDialogOpen] = useState(false);
  const [teamsDialogOpen, setTeamsDialogOpen] = useState(false);

  const { t } = useTranslation();

  const userSearchBtnRef = useRef(null);
  const teamsButton = useRef(null);

  const [date, setDate] = useState(initialDate);

  const userAppointments = useUserAppointments(userIdsInitial, date);

  const userAppointmentEvents = useMemo<MbscCalendarEvent[]>(() => {
    return userAppointments.data?.map((app) => {
      return {
        appointment: app,
        id: app.id,
        start: new Date(app.startDate),
        end: new Date(app.endDate),
        title: app.title,
        resource: app.userId,
      } as MbscCalendarEvent;
    });
  }, [userAppointments.data]);

  const resourcePlanningUtilizationTimeUnit = useSetting(
    settings.resourcePlanningUtilizationTimeUnit()
  );

  useEffect(() => {
    setDate(initialDate);
  }, [initialDate]);

  const startDate = DateTime.fromJSDate(date).startOf('year').toJSDate();
  const endDate = DateTime.fromJSDate(date).endOf('year').toJSDate();

  const { views, createView, deleteView } = useCalendarViews();

  const { data: userList } = useUsersById({
    idList: userIdsInitial,
  });

  const resourceInfoRes = useResourceInfo({
    endDate,
    startDate,
    userIdList: userList.map((user) => user.id),
  });

  const utilizationData = resourceInfoRes.data;

  const initialEvents = useUtilizationEvents({
    utilizationData,
  });

  useEffect(() => {
    setEvents([
      ...projectProcessEvents,
      ...initialEvents,
      ...userAppointmentEvents,
    ]);
  }, [userAppointmentEvents, projectProcessEvents, initialEvents]);

  const [createViewDialogOpen, setCreateViewDialogOpen] = useState(false);

  const onCreateView = useCallback(
    (name: string) => {
      createView({
        name,
        params: {
          appointmentStates: [],
          focusedUserIds: [],
          groupMode: 'vertical',
          range: 'month',
          userIds: userList.map((user) => user.id),
          planningAppointments: true,
        },
      });
    },
    [createView, userList]
  );

  const settingsPopoverState = usePopoverState('bottom');
  const settingsButtonRef = useRef(null);

  const { initiateDeleteAppointments } = useDeleteAppointments();

  const handleDelete = useCallback(
    (args: MbscEventDeleteEvent) => {
      const appointment = args.event.appointment;
      setEvents(cloneDeep(events));
      if (!appointment) {
        return;
      }
      initiateDeleteAppointments(
        selectedAppointments.map((appointment) => appointment.id),
        () => {
          userAppointments.refetch();
          resourceInfoRes.refetch();
        }
      );
    },
    [
      events,
      initiateDeleteAppointments,
      selectedAppointments,
      userAppointments,
      resourceInfoRes,
    ]
  );

  const resourcePlanningColoumnTimeUnit = useSetting(
    settings.resourcePlanningColoumnTimeUnit()
  );
  const renderResourceHeader = useCallback(() => {
    return (
      <Stack direction="row" p="0.125rem" pr={0} gap="0.5rem">
        <Popover {...settingsPopoverState}>
          <Stack padding="0 1rem 0.5rem 1rem" gap="0.5rem">
            <Divider title={t('SETTINGS.TABLES.LAYOUT')} />
            <ToggleButtonGroup
              exclusive
              value={resourcePlanningUtilizationTimeUnit.value}
              onChange={(_, value) => {
                resourcePlanningUtilizationTimeUnit.set(value);
              }}
            >
              <ToggleButton value="day">{t('COMMON.DAY')}</ToggleButton>
              <ToggleButton value="week">{t('COMMON.WEEK')}</ToggleButton>
              <ToggleButton value="month">{t('COMMON.MONTH')}</ToggleButton>
            </ToggleButtonGroup>
            <Divider title={t('COMMON.TIME_UNIT')} />
            <ToggleButtonGroup
              exclusive
              value={resourcePlanningColoumnTimeUnit.value}
              onChange={(_, value) => {
                resourcePlanningColoumnTimeUnit.set(value);
              }}
            >
              <ToggleButton value="minute">{t('COMMON.MINUTE')}</ToggleButton>
              <ToggleButton value="hour">{t('COMMON.HOUR')}</ToggleButton>
              <ToggleButton value="day">{t('COMMON.DAY')}</ToggleButton>
            </ToggleButtonGroup>
            <Divider title={t('COMMON.PROJECTPROCESS', { count: 2 })} />
            <CheckboxRadioItem
              label={t('RESSOURCEPLANNING.INCLUDE_SUB_PROJECTS')}
              checked={resourcePlanningIncludeSubProjects.value}
              onChange={(e) =>
                resourcePlanningIncludeSubProjects.set(e.target.checked)
              }
            />
            <Divider title={t('MASK.OPTIONS')} />
            <CheckboxRadioItem
              label={t('RESSOURCEPLANNING.INCLUDE_USER_APPOINTMENTS')}
              checked={resourcePlanningIncludeUserAppointments.value}
              onChange={(e) =>
                resourcePlanningIncludeUserAppointments.set(e.target.checked)
              }
            />
          </Stack>
        </Popover>

        <Box width="100%" ref={userSearchBtnRef}>
          <BaseActionButton
            icon={<Search />}
            title={t('COMMON.CHOOSE', { name: t('COMMON.USER_RESOURCE') })}
            onClick={() => {
              setUsersDialogOpen(true);
            }}
          />
        </Box>

        <Stack ref={teamsButton} direction="row" alignContent="center">
          <Tooltip title={t('COMMON.TEAMS')}>
            <IconButton
              size="small"
              onClick={() => {
                setTeamsDialogOpen(true);
              }}
            >
              <Groups />
            </IconButton>
          </Tooltip>
        </Stack>
        <Tooltip title={t('CALENDAR.VIEWS.SAVE_VIEW')}>
          <IconButton
            size="small"
            onClick={() => {
              setCreateViewDialogOpen(true);
            }}
            disabled={userList.length === 0}
          >
            <Save />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('COMMON.SETTINGS')}>
          <IconButton
            size="small"
            onClick={settingsPopoverState.handleClick}
            ref={settingsButtonRef}
          >
            <Settings />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('INPUTS.CLEAR_ALL')}>
          <IconButton
            disabled={userList.length === 0}
            size="small"
            onClick={() => {
              onUsersChange?.([]);
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    );
  }, [
    settingsPopoverState,
    t,
    resourcePlanningUtilizationTimeUnit,
    resourcePlanningColoumnTimeUnit,
    resourcePlanningIncludeSubProjects,
    resourcePlanningIncludeUserAppointments,
    userList.length,
    onUsersChange,
  ]);

  const renderResource = useCallback(
    (resource: MbscResource) => {
      const user = userList.find((x) => x.id === resource.id);
      return !user ? (
        <div style={{ padding: '0.5rem 0.5rem' }}>{resource.name}</div>
      ) : (
        <Stack
          direction="row"
          alignItems="center"
          p="0.25rem 0.5rem"
          gap="0.5rem"
          pr="0"
          width="100%"
        >
          <HookedUserIcon userId={user.id} size="m" />
          <Box width="100%">{user.displayName || user.shortName}</Box>
          <IconButton
            size="small"
            onClick={() => {
              onUsersChange?.(userList.filter((x) => x.id !== user.id));
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Stack>
      );
    },
    [userList, onUsersChange]
  );

  const resources = useMemo<MbscResource[]>(() => {
    return [
      ...projectProcessResources,
      ...userList.map(
        (user) => ({ id: user.id, name: user.displayName } as MbscResource)
      ),
    ];
  }, [userList, projectProcessResources]);

  const defaultColors = useColors({ resources });

  const colors = useMemo<MbscCalendarColor[]>(() => {
    const statusColors = utilizationData.map((item) => {
      const background =
        resourcePlanningUtilizationTimeUnit.value === 'day' &&
        item.utilizedValue >= item.planedValue
          ? !DateTime.fromISO(item.date).isWeekend
            ? 'var(--userShade7)'
            : undefined
          : undefined;

      return {
        start: item.date,
        end: item.date,
        resource: item.userId,
        background,
      } as MbscCalendarColor;
    });

    const highlightColors = resources
      .map((resource) => {
        return highlightedDateRanges
          .map((item) => {
            const result = [];
            const currentDate = new Date(item.start);
            while (currentDate <= new Date(item.end)) {
              result.push({
                start: DateTime.fromJSDate(currentDate)
                  .startOf('day')
                  .toJSDate(),
                end: DateTime.fromJSDate(currentDate).endOf('day').toJSDate(),
                resource: resource.id,
                background: 'var(--userShade5)',
              } as MbscCalendarColor);
              currentDate.setDate(currentDate.getDate() + 1);
            }

            return result;
          })
          .flat();
      })
      .flat();

    return [...defaultColors, ...highlightColors, ...statusColors];
  }, [
    resources,
    defaultColors,
    resourcePlanningUtilizationTimeUnit.value,
    highlightedDateRanges,
    utilizationData,
  ]);

  const handleOnEventCreated = useCallback(
    (args: MbscEventCreatedEvent) => {
      const { event } = args;

      const primaryUsers = (
        userList.length === 1
          ? userList
          : userList.filter((u) => u.id === event.resource)
      ).map((user) => ({ ...user, __typename: undefined }));

      const optionalUsers = userList.map((user) => ({
        ...user,
        __typename: undefined,
      }));

      const primaryAppointmentAttendeeList =
        mapUsersToAppointmentAttendees(primaryUsers);
      const optionalAppointmentAttendeeList =
        mapUsersToAppointmentAttendees(optionalUsers);

      const process =
        (event.process as ProjectProcess) ||
        selectedProcess ||
        focusedProjectProcess;
      const project =
        process?.project || selectedProcess?.project || focusedProject;

      const businessPartnerType = process?.customerId
        ? SdObjType.KUNDE
        : process?.supplierId
        ? SdObjType.LIEFERANT
        : undefined;
      const businessPartnerId = process?.customerId || process?.supplierId;

      const template: AppointmentTemplate = {
        appointmentAttendeeList: primaryAppointmentAttendeeList,
        startDate: DateTime.fromJSDate(event.start as Date)
          .set({ hour: 9 })
          .startOf('hour')
          .toISO(),
        endDate: DateTime.fromJSDate(event.end as Date)
          .set({ hour: 17 })
          .startOf('hour')
          .toISO(),
        isWholeDay: true,
        title:
          project || process
            ? `${process ? `${process.process} | ` : ''}${project?.name}`
            : event.title,
        project: project,
        projectId: project?.id,
        businessPartner: businessPartnerType
          ? {
              businessPartnerType,
              data: process?.customer || process?.supplier,
              id: businessPartnerId,
            }
          : undefined,
        businessPartnerId,
        businessPartnerType,
        projectProcess: process,
        projectProcessId: process?.id,
        isRealAppointment: false,
        projectProcessDuration: 1,
      };

      setAppointmentTemplate(template);
      setAttendeeSuggestions(optionalAppointmentAttendeeList);
      setDialogOpen(true);
      setEvents(cloneDeep(events));
    },
    [events, focusedProject, focusedProjectProcess, selectedProcess, userList]
  );

  const [resourceUtilizationInfoOpen, setResourceUtilizationInfoOpen] =
    useState(false);
  const [resourceUtilizationInfoData, setResourceUtilizationInfoData] =
    useState<{
      vacationList: Vacation[];
      absenceList: Sickness[];
      appointmentList: Appointment[];
    }>(null);

  const handleEventSelection = useCallback(
    (args: MbscSelectedEventsChangeEvent) => {
      setSelectedAppointments(
        args.events.filter((x) => x.appointment).map((x) => x.appointment)
      );
    },
    []
  );

  const handleEventClick = useCallback(
    (args: MbscEventClickEvent) => {
      if (args.event.process) {
        if (selectedProcess && selectedProcess.id === args.event.process.id) {
          setSelectedProcess(null);
        } else {
          setSelectedProcess(args.event.process);
        }
      }

      if (args.event.resource.toString().startsWith('project')) {
        return;
      }

      const data = args.event.data as ResourceUtilizationInfo[];
      if (!data) {
        return;
      }
      const params = {
        vacationList: data.flatMap((x) => x.vacationList),
        absenceList: data.flatMap((x) => x.absenceList),
        appointmentList: data.flatMap((x) => x.appointmentList),
      };

      setResourceUtilizationInfoData(params);
      setResourceUtilizationInfoOpen(true);
    },
    [selectedProcess]
  );

  return (
    <>
      <ResourceUtilizationInfoDialog
        open={resourceUtilizationInfoOpen}
        onClose={() => setResourceUtilizationInfoOpen(false)}
        {...resourceUtilizationInfoData}
      />
      <CreateViewDialog
        open={createViewDialogOpen}
        onClose={() => setCreateViewDialogOpen(false)}
        onConfirm={(name) => {
          setCreateViewDialogOpen(false);
          onCreateView(name);
        }}
        options={views.map((x) => x.name)}
      />
      <AppointmentQuickCreateDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          resourceInfoRes.refetch();
        }}
        onOpenMask={() => null}
        template={appointmentTemplate}
        attendeeSuggestions={attendeeSuggestions}
      />

      <Popover
        open={usersDialogOpen}
        onClose={() => setUsersDialogOpen(false)}
        anchorEl={userSearchBtnRef.current}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          [`.${popoverClasses.paper}`]: {
            height: 500,
          },
        }}
      >
        <UserCompilationPicker
          value={userList}
          onChange={onUsersChange}
          autoFocusTextInput
        />
      </Popover>

      <Popover
        open={teamsDialogOpen}
        onClose={() => setTeamsDialogOpen(false)}
        anchorEl={teamsButton.current}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <ViewsList
          views={views}
          activeView={null}
          onSelectView={(view) => {
            onUsersChange?.([]);
            setTimeout(() => {
              onUsersChange?.(view.params.userIds.map((id) => ({ id })));
              setTeamsDialogOpen(false);
            });
          }}
          onDeleteView={(view) => deleteView(view.id)}
          title={t('COMMON.TEAMS')}
        />
      </Popover>

      {showToolbar && (
        <ResourcesTimelineToolbar
          onDateChange={(e) => setDate?.(e)}
          unit={unit}
          date={date}
          zoom={zoom}
          onZoomChange={onZoomChange}
          onTimeUnitChange={onTimeUnitChange}
        />
      )}
      <ZoomableTimeline
        events={events}
        showControls={false}
        renderResourceHeader={renderResourceHeader}
        renderResource={renderResource}
        resources={resources}
        colors={colors}
        externalDrop
        dragToMove={false}
        onEventCreated={handleOnEventCreated}
        className={clsx(styles.resourceTimeline, className || '')}
        date={date}
        dragToCreate={true}
        newEventText={t('COMMON.NEW_PLANNING_APPOINTMENT')}
        onEventClick={handleEventClick}
        zoom={zoom}
        onEventDelete={handleDelete}
        compactView
        selectMultipleEvents
        onSelectedEventsChange={handleEventSelection}
        {...rest}
      />
    </>
  );
};
